import { withRouter } from 'react-router-dom';
import { useDependencies } from 'gw-portals-dependency-react';
import React, { useCallback, useEffect, useState, useContext } from 'react';
import { useTranslator } from '@jutro/locale';
import { ErrorCodes } from 'gw-portals-util-adac-js';
import { useModal } from '@jutro/components';
import { MetadataContent } from '@jutro/uiconfig';
import AdacConfigurableForm from '../../components/AdacConfigurableForm/AdacConfigurableForm';
import metadata from './AdacDocumentSigningPage.metadata.json5';
import styles from './AdacDocumentSigningPage.module.scss';
import messages from './AdacDocumentSigningPage.messages';
import { AdacConfirmationModal, AdacErrorModal, CommunicationVariablesContext } from '../../index';

function AdacDocumentSigningPage(props) {
    const {
        history,
        location
    } = props;
    const { ClaimService } = useDependencies('ClaimService');
    const { showModal } = useModal();
    const translator = useTranslator();
    const [htmlTemplate, setHtmlTemplate] = useState('');
    const [isSendingDocument, setIsSendingDocument] = useState(false);
    const [validationOutcome, setValidationOutcome] = useState({});
    const { communicationVariables } = useContext(CommunicationVariablesContext);
    const {
        token,
        sessionUUID
    } = communicationVariables;

    const handleError = useCallback((message, bool) => {
        showModal(
            <AdacErrorModal
                message={translator(message)}
                redirectEnabled={bool}
                onAfterClose={() => history.push({ pathname: '/callcenter' })}
            />
        );
    }, [showModal, token, translator]);

    useEffect(() => {
        const fetchDocumentTemplate = async () => {
            try {
                const contentString = await ClaimService.getDocumentTemplate(token, sessionUUID);
                if (!contentString) {
                    throw new Error('Empty content');
                }
                setHtmlTemplate(contentString);
            } catch (error) {
                handleError(messages.documentSignFailed, false);
            }
        };

        fetchDocumentTemplate();
    }, [ClaimService, history, token, sessionUUID]);

    const redirect = useCallback(() => {
        const redirectPath = '/documents';
        history.push({
            pathname: redirectPath,
            state: location.state
        });
    }, [history, token, sessionUUID]);

    const showModalAndRedirect = useCallback(async (message) => {
        await showModal(
            <AdacConfirmationModal
                message={translator(message)}
                confirmButtonText={translator(messages.close)}
            />
        );
        redirect();
    }, [showModal, translator]);

    const sendButtonClick = useCallback(async (signature, formData) => {
        if (!token || !sessionUUID) {
            return;
        }
        const signatureData = signature.current.toDataURL('image/png');
        setIsSendingDocument(true);

        ClaimService.signAndStoreDocument(token, {
            signatureData,
            documentData: JSON.stringify(formData),
            sessionUUID
        }).then((response) => {
            setIsSendingDocument(false);
            if (response.validationErrors && Object.keys(response.validationErrors).length > 0) {
                setValidationOutcome({
                    initialValidation: true,
                    validationErrors: response.validationErrors
                });
            } else {
                showModalAndRedirect(messages.submittedSuccessfully);
            }
        }).catch((error) => {
            setIsSendingDocument(false);
            if (error.appErrorCode === ErrorCodes.SESSION_EXPIRED) {
                history.push({
                    pathname: '/sessionexpired'
                });
            } else {
                handleError(messages.documentSignFailed, false);
            }
        });
    }, [
        ClaimService,
        showModalAndRedirect,
        handleError,
        setIsSendingDocument,
        redirect,
    ]);

    const resolvers = {
        resolveClassNameMap: styles
    };

    const overrides = {
        adacConfigurableForm: {
            visible: !!htmlTemplate,
            content: (
                <AdacConfigurableForm
                    sendButtonClick={sendButtonClick}
                    htmlTemplate={htmlTemplate}
                    isSendingDocument={isSendingDocument}
                    handleError={handleError}
                    validationOutcome={validationOutcome}
                    setValidationOutcome={setValidationOutcome}
                />
            )
        },
        loader: {
            visible: !htmlTemplate,
        }
    };

    return (
        <MetadataContent
            uiProps={metadata.pageContent}
            overrideProps={overrides}
            {...resolvers}
        />
    );
}

export default withRouter(AdacDocumentSigningPage);
